import React, { useEffect, useState, useCallback } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import {
  Spin,
  Input,
  Pagination,
  Table,
  Tabs,
  Drawer,
  Tag,
  Empty,
} from 'antd'
import styled from 'styled-components'
import {
  fetchCorporateAssessments,
  fetchCorporateAssessmentTypes,
  fetchCorporateAssessmentStates,
  fetchCorporateAssessmentStudents,
} from './actions'
import {
  selectAssessmentLoading,
  selectAssessmentError,
  selectAssessments,
  selectAssessmentPagination,
  selectAssessmentTypes,
  selectKpiData,
  selectStudents,
  selectStudentsPagination,
  selectStudentsLoading,
} from './selectors'

const { Search } = Input

const typeMap = {
  communication: 'Communication Assessment',
  behavior: 'Behavioral Assessment',
  aptitude: 'Aptitude',
  role_based: 'Role Based Assessment',
  technical_mcq: 'Technical MCQ',
  technical_coding: 'Technical Coding',
}

const normalizeType = (type) => (type || '').toLowerCase().replace(/[\s-]+/g, '_')

const formatDate = (dateString) => {
  if (!dateString) return '-'
  const date = new Date(dateString)
  return date.toLocaleDateString('en-GB', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  })
}

// Styled Components
const PageWrapper = styled.div`
  padding: 24px;
  background: #ffffffd8;
  height: calc(100vh - 48px);
  overflow-y: auto;
`

const PageTitle = styled.h1`
  font-size: 24px;
  font-weight: 600;
  color: #1a1a1a;
  margin-bottom: 24px;
`

const KpiRow = styled.div`
  display: flex;
  gap: 16px;
  margin-bottom: 24px;
  flex-wrap: wrap;
`

const KpiCard = styled.div`
  flex: 1;
  min-width: 200px;
  background: #fff;
  border-radius: 12px;
  padding: 20px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
  border: 1px solid #f0f0f0;
`

const KpiLabel = styled.div`
  font-size: 13px;
  color: #8c8c8c;
  margin-bottom: 8px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`

const KpiValue = styled.div`
  font-size: 28px;
  font-weight: 700;
  color: ${(props) => props.color || '#1a1a1a'};
`

const FilterRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
  flex-wrap: wrap;
  gap: 12px;
`

const ContentCard = styled.div`
  background: #fff;
  border-radius: 12px;
  padding: 20px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
  border: 1px solid #f0f0f0;
`

const AssessmentLink = styled.a`
  color: #3249D7;
  cursor: pointer;
  font-weight: 600;
  &:hover {
    text-decoration: underline;
  }
`

const BackLink = styled.a`
  color: #3249D7;
  cursor: pointer;
  margin-bottom: 16px;
  display: inline-block;
  font-size: 14px;
  &:hover {
    text-decoration: underline;
  }
`

const DrawerSection = styled.div`
  margin-bottom: 20px;
`

const DrawerLabel = styled.div`
  font-size: 12px;
  color: #8c8c8c;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-bottom: 4px;
`

const DrawerValue = styled.div`
  font-size: 16px;
  font-weight: 500;
  color: #1a1a1a;
`

const ScoreCard = styled.div`
  background: #fafafa;
  border-radius: 8px;
  padding: 12px 16px;
  margin-bottom: 8px;
  display: flex;
  justify-content: space-between;
  align-items: center;
`

const PaginationWrapper = styled.div`
  display: flex;
  justify-content: flex-end;
  margin-top: 16px;
`

const StyledTabs = styled(Tabs)`
  .ant-tabs-nav {
    margin-bottom: 0;
    background: #F5F5F5;
    border-radius: 12px;
  }
  .ant-tabs-tab {
    padding: 12px 20px !important;
    font-weight: 400 !important;
    &.ant-tabs-tab-active {
      background: #3D5AFE !important;
      border-radius: 12px;
      .ant-tabs-tab-btn {
        color: #FFFFFF !important;
        font-weight: 700 !important;
      }
    }
  }
  .ant-tabs-ink-bar {
    display: none;
  }
  .ant-tabs-nav::before {
    border-bottom: none;
  }
`

const StatusTag = ({ status }) => {
  const colorMap = {
    'ongoing': 'processing',
    'upcoming': 'default',
    'expired - completed': 'success',
    'expired - lapsed': 'error',
    'completed': 'success',
  }
  const color = colorMap[(status || '').toLowerCase()] || 'default'
  return <Tag color={color}>{status || 'N/A'}</Tag>
}

const CorporateAssessment = () => {
  const dispatch = useDispatch()

  // Redux state
  const loading = useSelector(selectAssessmentLoading)
  const error = useSelector(selectAssessmentError)
  const assessments = useSelector(selectAssessments)
  const pagination = useSelector(selectAssessmentPagination)
  const assessmentTypes = useSelector(selectAssessmentTypes)
  const kpiData = useSelector(selectKpiData)
  const students = useSelector(selectStudents)
  const studentsPagination = useSelector(selectStudentsPagination)
  const studentsLoading = useSelector(selectStudentsLoading)

  // Local state
  const [activeTab, setActiveTab] = useState('all')
  const [searchText, setSearchText] = useState('')
  const [selectedAssessment, setSelectedAssessment] = useState(null)
  const [studentSearch, setStudentSearch] = useState('')
  const [reportDrawerVisible, setReportDrawerVisible] = useState(false)
  const [selectedStudent, setSelectedStudent] = useState(null)

  // Initial data fetching
  useEffect(() => {
    dispatch(fetchCorporateAssessmentTypes())
    dispatch(fetchCorporateAssessmentStates())
    dispatch(fetchCorporateAssessments())
  }, [dispatch])

  // Fetch assessments when tab changes
  const loadAssessments = useCallback(
    (page = 1) => {
      const params = { page, pageSize: pagination.pageSize }
      if (activeTab !== 'all') {
        params.type_name = activeTab
      }
      if (searchText) {
        params.search = searchText
      }
      dispatch(fetchCorporateAssessments(params))
    },
    [dispatch, activeTab, searchText, pagination.pageSize]
  )

  useEffect(() => {
    loadAssessments(1)
  }, [activeTab]) // eslint-disable-line react-hooks/exhaustive-deps

  const handleSearch = (value) => {
    setSearchText(value)
    const params = { page: 1, pageSize: pagination.pageSize }
    if (activeTab !== 'all') {
      params.type_name = activeTab
    }
    if (value) {
      params.search = value
    }
    dispatch(fetchCorporateAssessments(params))
  }

  const handlePageChange = (page) => {
    loadAssessments(page)
  }

  // Assessment click -> show students
  const handleAssessmentClick = (assessment) => {
    setSelectedAssessment(assessment)
    setStudentSearch('')
    dispatch(
      fetchCorporateAssessmentStudents({
        assessmentCorporateMapId: assessment.assessmentCorporateMapId,
        page: 1,
      })
    )
  }

  const handleBackToAssessments = () => {
    setSelectedAssessment(null)
    setStudentSearch('')
  }

  const handleStudentSearch = (value) => {
    setStudentSearch(value)
    dispatch(
      fetchCorporateAssessmentStudents({
        assessmentCorporateMapId: selectedAssessment.assessmentCorporateMapId,
        page: 1,
        search: value,
      })
    )
  }

  const handleStudentPageChange = (page) => {
    dispatch(
      fetchCorporateAssessmentStudents({
        assessmentCorporateMapId: selectedAssessment.assessmentCorporateMapId,
        page,
        search: studentSearch || undefined,
      })
    )
  }

  // Student click -> open report drawer
  const handleStudentClick = (student) => {
    setSelectedStudent(student)
    setReportDrawerVisible(true)
  }

  const handleCloseReportDrawer = () => {
    setReportDrawerVisible(false)
    setSelectedStudent(null)
  }

  // Assessment table columns
  const assessmentColumns = [
    {
      title: 'STATUS',
      dataIndex: 'status',
      key: 'status',
      width: 120,
      render: (status) => <StatusTag status={status} />,
    },
    {
      title: 'ASSESSMENT TYPE / NAME',
      key: 'name',
      width: 230,
      render: (_, record) => {
        const normalized = normalizeType(record.assessmentType)
        return (
          <div>
            <div style={{ fontSize: 12, color: '#8c8c8c' }}>
              {typeMap[normalized] || record.assessmentType || '-'}
            </div>
            <AssessmentLink onClick={() => handleAssessmentClick(record)}>
              {record.name || 'Untitled Assessment'}
            </AssessmentLink>
          </div>
        )
      },
    },
    {
      title: 'ASSMT. SENT / TAKEN',
      key: 'sentTaken',
      width: 160,
      render: (_, record) => (
        <span>
          {record.assessmentsSent || 0} / {record.assessmentsTaken || 0}
        </span>
      ),
    },
    {
      title: 'Expired Assmts.',
      dataIndex: 'expiredAssessments',
      key: 'expired',
      width: 120,
      render: (val) => val || 0,
    },
    {
      title: 'CANDIDATES',
      dataIndex: 'totalCandidates',
      key: 'candidates',
      width: 120,
      render: (val) => val || 0,
    },
    {
      title: 'Dates',
      key: 'dates',
      width: 200,
      render: (_, record) => (
        <span>
          {formatDate(record.startDate)} - {formatDate(record.endDate)}
        </span>
      ),
    },
  ]

  // Student table columns
  const studentColumns = [
    {
      title: 'Name',
      key: 'name',
      render: (_, record) => (
        <AssessmentLink onClick={() => handleStudentClick(record)}>
          {record.name || record.primaryEmail || 'N/A'}
        </AssessmentLink>
      ),
    },
    {
      title: 'Email',
      dataIndex: 'primaryEmail',
      key: 'email',
    },
    {
      title: 'Status',
      key: 'status',
      width: 120,
      render: (_, record) => {
        if (record.submitted) return <Tag color="success">Completed</Tag>
        if (record.attempted) return <Tag color="processing">In Progress</Tag>
        return <Tag color="default">Not Started</Tag>
      },
    },
    {
      title: 'Total Score',
      dataIndex: 'totalScore',
      key: 'totalScore',
      width: 120,
      render: (val) => (val !== undefined && val !== null ? Math.round(val * 100) / 100 : '-'),
    },
    {
      title: 'Category Scores',
      key: 'sectionScores',
      render: (_, record) => {
        const scores = record.sectionScores || {}
        const entries = Object.entries(scores).filter(([, v]) => v !== undefined && v !== null)
        if (!entries.length) return '-'
        return (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
            {entries.map(([key, value]) => (
              <Tag key={key} style={{ marginBottom: 2 }}>
                {key}: {Math.round(value * 100) / 100}
              </Tag>
            ))}
          </div>
        )
      },
    },
  ]

  // Report drawer content
  const renderReportDrawer = () => {
    const student = selectedStudent || {}
    const studentName = student.name || student.primaryEmail || 'N/A'
    const scores = student.sectionScores || {}
    const scoreEntries = Object.entries(scores).filter(([, v]) => v !== undefined && v !== null)

    return (
      <Drawer
        title="Assessment Report"
        placement="right"
        width={480}
        onClose={handleCloseReportDrawer}
        visible={reportDrawerVisible}
      >
        <DrawerSection>
          <DrawerLabel>Student Name</DrawerLabel>
          <DrawerValue>{studentName}</DrawerValue>
        </DrawerSection>

        <DrawerSection>
          <DrawerLabel>Email</DrawerLabel>
          <DrawerValue>{student.primaryEmail || 'N/A'}</DrawerValue>
        </DrawerSection>

        <DrawerSection>
          <DrawerLabel>Assessment Type</DrawerLabel>
          <DrawerValue>
            {typeMap[normalizeType(student.assessmentType)] || student.assessmentType || 'N/A'}
          </DrawerValue>
        </DrawerSection>

        <DrawerSection>
          <DrawerLabel>Status</DrawerLabel>
          <DrawerValue>
            {student.submitted ? 'Completed' : student.attempted ? 'In Progress' : 'Not Started'}
          </DrawerValue>
        </DrawerSection>

        <DrawerSection>
          <DrawerLabel>Total Score</DrawerLabel>
          <DrawerValue style={{ color: '#3249D7', fontSize: 24 }}>
            {student.totalScore !== undefined && student.totalScore !== null
              ? Math.round(student.totalScore * 100) / 100
              : 'N/A'}
          </DrawerValue>
        </DrawerSection>

        <DrawerSection>
          <DrawerLabel>Category Scores</DrawerLabel>
          {scoreEntries.length > 0 ? (
            scoreEntries.map(([category, score]) => (
              <ScoreCard key={category}>
                <span style={{ color: '#595959', textTransform: 'capitalize' }}>{category}</span>
                <span style={{ fontWeight: 600 }}>{Math.round(score * 100) / 100}</span>
              </ScoreCard>
            ))
          ) : (
            <DrawerValue style={{ color: '#8c8c8c' }}>No category scores available</DrawerValue>
          )}
        </DrawerSection>

        {student.submittedAt && (
          <DrawerSection>
            <DrawerLabel>Submission Date</DrawerLabel>
            <DrawerValue>{formatDate(student.submittedAt)}</DrawerValue>
          </DrawerSection>
        )}
      </Drawer>
    )
  }

  // Student list view
  if (selectedAssessment) {
    const assessmentName = selectedAssessment.name || 'Untitled Assessment'
    const normalized = normalizeType(selectedAssessment.assessmentType)
    const assessmentTypeDisplay = typeMap[normalized] || selectedAssessment.assessmentType || ''

    return (
      <PageWrapper>
        <BackLink onClick={handleBackToAssessments}>
          &larr; Back to Assessments
        </BackLink>
        <PageTitle>
          {assessmentName}
          {assessmentTypeDisplay && (
            <span style={{ fontSize: 14, color: '#8c8c8c', marginLeft: 12, fontWeight: 400 }}>
              ({assessmentTypeDisplay})
            </span>
          )}
        </PageTitle>

        <ContentCard>
          <FilterRow>
            <Search
              placeholder="Search students..."
              allowClear
              onSearch={handleStudentSearch}
              style={{ maxWidth: 320 }}
            />
            <span style={{ color: '#8c8c8c' }}>
              {studentsPagination.totalCount} student{studentsPagination.totalCount !== 1 ? 's' : ''}
            </span>
          </FilterRow>

          <Table
            columns={studentColumns}
            dataSource={students}
            rowKey={(record) => record.assessmentAssignedStudentId || record.primaryEmail}
            loading={studentsLoading}
            pagination={false}
            locale={{ emptyText: <Empty description="No students found" /> }}
          />

          {studentsPagination.totalCount > studentsPagination.pageSize && (
            <PaginationWrapper>
              <Pagination
                current={studentsPagination.currentPage}
                total={studentsPagination.totalCount}
                pageSize={studentsPagination.pageSize}
                onChange={handleStudentPageChange}
                showSizeChanger={false}
              />
            </PaginationWrapper>
          )}
        </ContentCard>

        {renderReportDrawer()}
      </PageWrapper>
    )
  }

  // Build tab items
  const tabItems = [
    { label: 'ALL', key: 'all' },
    ...assessmentTypes.map((t) => ({
      label: typeMap[normalizeType(t.typeName)] || t.typeName,
      key: t.typeName?.toLowerCase() || t.key,
    })),
  ]

  // Main assessments view
  return (
    <PageWrapper>
      <PageTitle>Assessment Dashboard</PageTitle>

      {/* KPI Cards */}
      <KpiRow>
        <KpiCard>
          <KpiLabel>Total Candidates</KpiLabel>
          <KpiValue color="#3249D7">{kpiData.totalCandidates}</KpiValue>
        </KpiCard>
        <KpiCard>
          <KpiLabel>Total Assessments Sent</KpiLabel>
          <KpiValue>{kpiData.totalAssessmentsSent}</KpiValue>
        </KpiCard>
        <KpiCard>
          <KpiLabel>Total Assessments Taken</KpiLabel>
          <KpiValue>{kpiData.totalAssessmentsTaken}</KpiValue>
        </KpiCard>
        <KpiCard>
          <KpiLabel>Total Practice Tests</KpiLabel>
          <KpiValue>{kpiData.totalPracticeTests}</KpiValue>
        </KpiCard>
      </KpiRow>

      {/* Type Tabs */}
      <div style={{ marginBottom: 24 }}>
        <StyledTabs
          activeKey={activeTab}
          onChange={(key) => setActiveTab(key)}
          items={tabItems}
          type="card"
        />
      </div>

      <ContentCard>
        <FilterRow>
          <Search
            placeholder="Search assessments..."
            allowClear
            onSearch={handleSearch}
            style={{ maxWidth: 320 }}
          />
          <span style={{ color: '#8c8c8c' }}>
            Showing {assessments.length} of {pagination.totalCount}
          </span>
        </FilterRow>

        {error && (
          <div style={{ color: '#ff4d4f', marginBottom: 16 }}>{error}</div>
        )}

        <Table
          columns={assessmentColumns}
          dataSource={assessments}
          rowKey={(record) => record.assessmentCorporateMapId}
          loading={loading}
          pagination={false}
          locale={{ emptyText: <Empty description="No assessments found" /> }}
        />

        {pagination.totalCount > pagination.pageSize && (
          <PaginationWrapper>
            <Pagination
              current={pagination.currentPage}
              total={pagination.totalCount}
              pageSize={pagination.pageSize}
              onChange={handlePageChange}
              showSizeChanger={false}
            />
          </PaginationWrapper>
        )}
      </ContentCard>

      {renderReportDrawer()}
    </PageWrapper>
  )
}

export default CorporateAssessment
